/**
 * The pages of ONE vendor invoice, chosen as several photos or files, in the order
 * they will be joined (BUSINESS_RULES#vendor-invoice-multi-page-upload).
 *
 * VI-4, PROVISIONAL O-18. Staff photographed a long invoice page by page and
 * joined the photos on an outside "JPG to PDF" website. The server now joins them
 * (POST /vendor-invoices/upload-pages); this module keeps the page order the
 * reviewer sets and prepares each photo for that join.
 */

export type UploadPage = { id: string; file: File };

// Same name, size and modified time: the same file picked twice.
const fileKey = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;

/**
 * Append picked files after the pages already chosen, so a phone camera can add
 * one page at a time. A file that is not a PDF or image is refused, and the same
 * file picked again is not added twice.
 */
export function addPickedPages(
  current: UploadPage[],
  picked: File[],
  isAccepted: (f: File) => boolean
): { pages: UploadPage[]; refused: File[]; repeated: File[] } {
  const seen = new Set(current.map((p) => p.id));
  const pages = [...current];
  const refused: File[] = [];
  const repeated: File[] = [];
  for (const file of picked) {
    if (!isAccepted(file)) {
      refused.push(file);
      continue;
    }
    const id = fileKey(file);
    if (seen.has(id)) {
      repeated.push(file);
      continue;
    }
    seen.add(id);
    pages.push({ id, file });
  }
  return { pages, refused, repeated };
}

/** Swap a page with its neighbour; a move past either end changes nothing. */
export function movePage(pages: UploadPage[], index: number, delta: -1 | 1): UploadPage[] {
  const to = index + delta;
  if (index < 0 || index >= pages.length || to < 0 || to >= pages.length) return pages;
  const next = [...pages];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}

// Long edge of a photo as sent, in pixels — the cap this module exists to get
// right (OCR-legibility incident, page-2 fields coming back empty).
//
// A4's long edge is 11.69 inches, and the widely used floor for reliable OCR of
// small print is 200 DPI. The old cap, 2200px, was 2200 / 11.69 = 188 DPI —
// under that floor, and dense page-2 tables (line-item rows, small type, VAT
// footers) sit right under it: the suspected reason those fields came back
// empty from extraction.
//
// 2600px / 11.69" = 222 DPI, clearing the floor with real margin. A 12 MP phone
// photo (4032px long edge) still gets shrunk by more than a third, so this does
// not remove the size cap's point — see pageForUpload's size note and
// uploadPages.test.ts, which pins this arithmetic so the number cannot quietly
// drift back down to "optimise" upload size.
export const A4_LONG_EDGE_IN = 11.69;
export const MAX_EDGE_PX = 2600;

/**
 * The factor a photo this size is shrunk by before it is re-encoded.
 * `Math.min(1, ...)` is load-bearing: a photo already at or under MAX_EDGE_PX
 * on its long edge must never be scaled UP past its own captured detail.
 */
export function scaleForUpload(width: number, height: number): number {
  return Math.min(1, MAX_EDGE_PX / Math.max(width, height));
}

/**
 * A page as it is sent to the join: a PDF unchanged; a photo turned upright from
 * its EXIF orientation, shrunk to MAX_EDGE_PX and re-encoded as JPEG, because the
 * server joins only PDF, JPEG and PNG. A photo this browser cannot open (an iPhone
 * HEIC photo outside Safari) throws with a message saying what to do.
 */
export async function pageForUpload(file: File): Promise<File> {
  if (!String(file.type || "").toLowerCase().startsWith("image/")) return file;
  const unreadable = () =>
    new Error(
      `"${file.name}" could not be opened by this browser. If it is an iPhone (HEIC) photo, send it as JPEG ` +
        `(on the iPhone: Settings > Camera > Formats > Most Compatible), or remove it, then try again.`
    );

  let bitmap: ImageBitmap;
  try {
    // "from-image" (apply EXIF rotation) is the current spec value; this TypeScript
    // DOM lib only knows the older "flipY" | "none", hence the cast.
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" } as unknown as ImageBitmapOptions);
  } catch {
    throw unreadable();
  }
  const scale = scaleForUpload(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw unreadable();
  // JPEG has no transparency: a transparent PNG would otherwise turn black.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  // Quality left at 0.85, deliberately: the loss at this end of the JPEG curve
  // is chroma/DCT quantization noise, not missing pixels, so it was not what
  // starved small print of legible strokes — MAX_EDGE_PX (too few pixels) was.
  // Raising quality further would compound the size increase from 2600px above
  // for a legibility return the resolution fix already covers.
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
  if (!blob) throw unreadable();
  return new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.jpg`, { type: "image/jpeg" });
}
